
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AgentDefinition, AgentExecutionResponse } from "@/models/agent-runtime";
import { toast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { ApiKeyService } from "@/services/api-key-service";
import { supabase } from "@/integrations/supabase/client";

interface AgentRuntimeFormProps {
  agent: AgentDefinition;
  onExecutionComplete?: (result: AgentExecutionResponse) => void;
}

export function AgentRuntimeForm({ agent, onExecutionComplete }: AgentRuntimeFormProps) {
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [hasApiKey, setHasApiKey] = useState<boolean | null>(null);
  const [checkingKey, setCheckingKey] = useState(true);

  useEffect(() => {
    // Check if the API key for the required provider is available
    const checkApiKey = async () => {
      setCheckingKey(true);
      try {
        const apiKeys = await ApiKeyService.getApiKeys();
        const requiredProvider = agent.llm_provider || "openai";
        const hasKey = apiKeys.some(key => key.id.toLowerCase() === requiredProvider.toLowerCase());
        setHasApiKey(hasKey);
      } catch (error) {
        console.error("Error checking API key:", error);
        setHasApiKey(false);
      } finally {
        setCheckingKey(false);
      }
    };
    
    checkApiKey();
  }, [agent.llm_provider]);

  const handleInputChange = (key: string, value: any) => {
    setInputs(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    const requiredInputs = Object.entries(agent.input_schema)
      .filter(([_, schema]) => schema.required)
      .map(([key]) => key);
      
    const missingInputs = requiredInputs.filter(key => !inputs[key]);
    
    if (missingInputs.length > 0) {
      toast({
        variant: "destructive",
        title: "Missing required inputs",
        description: `Please provide values for: ${missingInputs.join(", ")}`
      });
      return;
    }
    
    if (!hasApiKey) {
      toast({
        variant: "destructive",
        title: "API key not configured",
        description: `Please add your ${agent.llm_provider} API key in the API Keys tab`
      });
      return;
    }

    setLoading(true);
    try {
      console.log("Executing agent with inputs:", inputs);
      
      // First, store execution in the agent_executions table
      const { data: newExecution, error: execError } = await supabase
        .from('agent_executions')
        .insert({
          agent_id: agent.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          inputs,
          status: 'running'
        })
        .select('id')
        .single();
        
      if (execError) {
        throw new Error(execError.message || "Failed to create execution record");
      }
      
      // Call the run-agent edge function
      const { data, error } = await supabase.functions.invoke("run-agent", {
        body: { 
          agentId: agent.id,
          prompt: JSON.stringify(inputs), 
          model: agent.llm_config.model,
          temperature: agent.llm_config.temperature,
          maxTokens: agent.llm_config.max_tokens,
          executionId: newExecution?.id
        }
      });
      
      if (error) {
        throw new Error(error.message || "Failed to execute agent");
      }
      
      // Update the execution record
      await supabase
        .from('agent_executions')
        .update({
          status: data.success ? 'completed' : 'error',
          output: data.result || null,
          execution_time_ms: data.executionTime || 0,
          token_usage: data.tokenUsage || {
            prompt: 0,
            completion: 0,
            total: 0
          },
          error: data.success ? null : (data.error || "Unknown error"),
          logs: data.logs || [],
          completed_at: new Date().toISOString()
        })
        .eq('id', newExecution?.id);
      
      // Real execution response
      const response: AgentExecutionResponse = {
        success: data.success,
        output: data.result || "No output was generated",
        executionTime: data.executionTime || 0,
        tokenUsage: data.tokenUsage || {
          prompt: 0,
          completion: 0,
          total: 0
        },
        logs: data.logs || [],
        timestamp: new Date().toISOString()
      };

      if (onExecutionComplete) {
        onExecutionComplete(response);
      }
      
      toast({
        title: "Agent executed",
        description: "Agent has processed your request successfully",
      });
    } catch (error) {
      console.error("Error executing agent:", error);
      toast({
        variant: "destructive",
        title: "Execution failed",
        description: error instanceof Error ? error.message : "Failed to execute the agent. Please try again.",
      });
      
      // If there's an error, pass an error response
      if (onExecutionComplete) {
        onExecutionComplete({
          success: false,
          output: null,
          executionTime: 0,
          error: error instanceof Error ? error.message : "Failed to execute agent. The service might be temporarily unavailable.",
          logs: ["Error encountered during execution"],
          timestamp: new Date().toISOString()
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{agent.name}</CardTitle>
      </CardHeader>
      <CardContent>
        {checkingKey ? (
          <div className="flex justify-center p-4">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin h-6 w-6 border-2 border-current border-t-transparent rounded-full" />
              <p className="text-sm text-muted-foreground">Checking API key...</p>
            </div>
          </div>
        ) : !hasApiKey ? (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-4 mb-4">
            <div className="flex items-start">
              <div>
                <h3 className="text-amber-800 dark:text-amber-400 font-medium">API Key Required</h3>
                <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                  This agent requires a {agent.llm_provider} API key. 
                  Please add your API key in the API Keys tab.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  asChild 
                  className="mt-2 text-amber-700 border-amber-300 hover:bg-amber-100 hover:text-amber-800"
                >
                  <Link to="/run-agent?tab=api-keys">Configure API Key</Link>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {Object.entries(agent.input_schema).map(([key, schema]) => (
              <div key={key}>
                <Label htmlFor={key}>{key}</Label>
                {schema.type === "string" && schema.enum ? (
                  <Select 
                    defaultValue={schema.enum[0]} 
                    onValueChange={(value) => handleInputChange(key, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {schema.enum.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={key}
                    type="text"
                    placeholder={schema.description}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    required={schema.required}
                  />
                )}
              </div>
            ))}
            <Button type="submit" disabled={loading || !hasApiKey}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                  Processing...
                </span>
              ) : (
                "Execute Agent"
              )}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
